import { deepCopy } from '../utils/deep';

export default (context, inject) => {
  const META_EXPIRES_IN_MINUTES = 15;

  // Aanpassen naar https://marmelab.com/react-admin/DataProviders.html

  class ApiDataProvider {
    constructor (baseEndpoint) {
      this.baseEndpoint = baseEndpoint;
      this.identifier = null;
      this.endpoint = null;
      this.defaultPageItems = 15;
      this.filters = null;
      this.cachedResponses = {};
      this.beforeFetchCallback = null;
    }

    beforeFetch (callback) {
      if (typeof callback === 'function') {
        this.beforeFetchCallback = callback;
      }

      return this;
    }

    getEndpoint () {
      let url = `${this.baseEndpoint}${this.endpoint}`;
      url += this.identifier ? `/${this.identifier}` : '';
      url += this.action ? `/${this.action}` : '';

      return url;
    }

    setCachedResponse (response) {
      this.cachedResponses[this.getEndpoint()] = { ...response, fromCache: true };

      return this;
    }

    getCachedResponse () {
      return this.cachedResponses[this.getEndpoint()] || {};
    }

    shouldFetch () {
      const cachedResponse = this.getCachedResponse();

      if (this.forceFetch === true) {
        return true;
      }

      if (!!cachedResponse.expiresIn === false) {
        return true;
      }

      if (new Date(cachedResponse.expiresIn).getTime() < (new Date()).getTime()) {
        return true;
      }

      const filterHasChanged = (
        JSON.stringify(cachedResponse.filters) !== JSON.stringify(this.filters)
      );

      if (
        this.identifier === null
        && (!cachedResponse.filters || filterHasChanged)
      ) {
        return true;
      }

      return false;
    }

    /**
     * @deprecated
     * @param endpoint
     * @param filters
     * @param forceFetch
     * @returns {Promise<unknown>|*}
     */
    fetchAll (endpoint = '', filtersArg = {}, forceFetch = false) {
      this.endpoint = endpoint;
      this.identifier = null;

      const filters = this.resolveFilters(filtersArg);

      this.forceFetch = forceFetch;
      this.filters = filters;

      if (this.shouldFetch() === false) {
        return Promise.resolve(this.getCachedResponse());
      }

      if (typeof this.beforeFetchCallback === 'function') {
        this.beforeFetchCallback();
      }

      return context.$api
        .get(this.getEndpoint(), this.mapFiltersToParams(filters))
        .then((response) => {
          const minutes = new Date().getMinutes() + META_EXPIRES_IN_MINUTES;
          const expiresIn = new Date(new Date().setMinutes(minutes)).toISOString();
          const cachedResponse = {
            items: response.data['hydra:member'],
            totalItems: response.data['hydra:totalItems'],
            filters,
            response,
            fromCache: false,
            expiresIn,
          };

          this.setCachedResponse(cachedResponse);

          return cachedResponse;
        });
    }

    /**
     * @deprecated
     * @param endpoint
     * @param uuid
     * @param forceFetch
     * @returns {Promise<unknown>|*}
     */
    fetchObject (endpoint, { uuid }, forceFetch = false) {
      this.endpoint = endpoint;
      this.identifier = uuid;
      this.forceFetch = forceFetch;

      if (this.shouldFetch() === false) {
        return Promise.resolve(this.getCachedResponse());
      }

      if (typeof this.beforeFetchCallback === 'function') {
        this.beforeFetchCallback();
      }

      return context.$api.get(this.getEndpoint())
        .then((response) => {
          const minutes = new Date().getMinutes() + META_EXPIRES_IN_MINUTES;
          const expiresIn = new Date(new Date().setMinutes(minutes)).toISOString();
          const cachedResponse = {
            item: response.data,
            response,
            fromCache: false,
            expiresIn,
          };

          this.setCachedResponse(cachedResponse);

          return cachedResponse;
        });
    }

    resolveFilters (filtersArg) {
      const filters = { ...filtersArg };

      if (typeof filters.search === 'undefined') {
        filters.search = null;
      }

      if (typeof filters.currentPage === 'undefined') {
        filters.currentPage = 1;
      }

      if (typeof filters.pageItems === 'undefined') {
        filters.pageItems = this.defaultPageItems;
      }

      return filters;
    }

    camelToSnakeCase (str) {
      const result = str.replace(/([A-Z])/g, ' $1');

      return result.split(' ').join('_').toLowerCase();
    }

    mapFiltersToParams (filters = {}) {
      const params = {};

      for (let k in filters) {
        if (filters[k] === null) {
          continue;
        }

        // tmp patch for backward compatibility
        if (k === 'currentPage') {
          k = 'page';
        }

        // todo: Make this the default to keep filter props as original defined
        if (k.includes('[')) {
          params[k] = filters[k];
        } else {
          params[this.camelToSnakeCase(k)] = filters[k];
        }
      }

      return params;
    }

    getList (endpoint = '', filters = {}, setup = {}) {
      this.prepareRequest(endpoint, setup);

      return context.$api
        .get(this.getEndpoint(), this.mapFiltersToParams(filters), setup.config)
        .then((response) => {
          if (response.headers['content-type'].includes('application/ld+json')) {
            return {
              items: response.data['hydra:member'],
              totalItems: response.data['hydra:totalItems'],
            };
          } else if (response.data.data !== undefined) {
            return {
              items: response.data.data,
              totalItems: response.data.meta.totalItems,
            };
          }

          return response.data;
        });
    }

    getOne (endpoint = '', setup = {}) {
      this.prepareRequest(endpoint, setup);

      return context.$api.get(this.getEndpoint(), setup.params, setup.config);
    }

    create (endpoint = '', setup = {}) {
      this.prepareRequest(endpoint, setup);

      return context.$api.post(this.getEndpoint(), setup.data, setup.config);
    }

    update (endpoint = '', setup = {}, usePatch = false) {
      this.prepareRequest(endpoint, setup);

      if (usePatch) {
        return context.$api.patch(this.getEndpoint(), setup.data, setup.config);
      }

      return context.$api.put(this.getEndpoint(), setup.data, setup.config);
    }

    delete (endpoint = '', setup = {}) {
      this.prepareRequest(endpoint, setup);

      return context.$api.delete(this.getEndpoint(), setup.config);
    }

    getFilenameFromResponseHeader (response = {}) {
      if (!(response.headers && response.headers['content-disposition'])) {
        return null;
      }

      const match = response.headers['content-disposition'].match(/filename="(.+)"/);

      return match ? match[1] : null;
    }

    getFile (endpoint = '', setupArg = {}) {
      const setup = deepCopy(setupArg);

      this.prepareRequest(endpoint, setup);

      setup.config = {
        ...setup.config,
        responseType: 'blob',
      };

      let filename = setup.filename;
      delete setup.filename;

      return context.$api.get(this.getEndpoint(), setup.params, setup.config)
        .then((response) => {
          if (!filename) {
            filename = this.getFilenameFromResponseHeader(response) || 'download';
          }

          const link = document.createElement('a');
          const blob = new Blob([response.data]);
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();

          return response;
        })
        .catch((error) => console.error(new Error(error.message)));
    }

    uploadFiles (setup = {}) {
      this.prepareRequest('', setup);
      const formData = new FormData();
      const config = setup.config || {};
      const headers = { 'Content-Type': 'multipart/form-data' };

      setup.files.forEach((file) => {
        formData.append('file', file);
      });

      return context.$api.post(this.getEndpoint(), formData, {
        ...config,
        headers,
      });
    }

    prepareRequest (endpoint = '', setup = {}) {
      this.endpoint = endpoint;
      this.identifier = setup.identifier || setup.uuid || setup.id || null;
      this.action = setup.action || null;
    }
  }

  const dataProviders = [];
  const getDataProvider = (endpoint) => {
    if (dataProviders[endpoint] !== undefined) {
      return dataProviders[endpoint];
    }

    return (dataProviders[endpoint] = new ApiDataProvider(endpoint));
  };

  inject('getDataProvider', getDataProvider);
};
